import React, { useState, useEffect, useMemo } from "react";
import { Layers, X, Search } from "lucide-react";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import AuthService from "../../services/Auth.service";
import ApiService from "../../services/Api.service";
import toast from "react-hot-toast";

const AddDispositionGroupModel = ({ isOpen, onClose, onSave, itemData }) => {
  const [name, setName] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Map of value -> label for quick display lookup
  const optionsMap = useMemo(() => {
    const map = {};
    availableOptions.forEach((opt) => {
      if (opt.value) {
        map[opt.value] = opt.label;
      }
    });
    return map;
  }, [availableOptions]);

  const fetchDispositions = async () => {
    try {
      setLoadingOptions(true);
      const options = [];
      const seenValues = new Set();

      const addOption = (value, label) => {
        const valStr = String(value || "").trim();
        if (!valStr || seenValues.has(valStr)) return;
        seenValues.add(valStr);
        options.push({ value: valStr, label: label || valStr });
      };

      // 1. Primary: ApiService.getDispositionListOptions() returns [{ value, label }]
      try {
        const optionRes = await ApiService.getDispositionListOptions();
        if (optionRes?.data) {
          const rawList =
            optionRes.data.dispositions ||
            (Array.isArray(optionRes.data) ? optionRes.data : optionRes.data.data) ||
            [];

          rawList.forEach((item) => {
            if (item && item.value) {
              addOption(item.value, item.label);
            }
          });
        }
      } catch (err) {
        console.warn("ApiService.getDispositionListOptions error:", err);
      }

      // 2. Fallback: AuthService.getDispositionList()
      if (options.length === 0) {
        try {
          const adminRes = await AuthService.getDispositionList({ page: 1, size: 100 });
          const rawList = adminRes?.data?.data || (Array.isArray(adminRes?.data) ? adminRes.data : []);
          rawList.forEach((item) => {
            const val = item?.id || item?._id || item?.value;
            if (val) {
              const label = item.sub_disposition
                ? `${item.disposition} - ${item.sub_disposition}`
                : item.disposition || val;
              addOption(val, label);
            }
          });
        } catch (err) {
          console.warn("AuthService.getDispositionList error:", err);
        }
      }

      setAvailableOptions(options);
    } catch (err) {
      console.error("Failed to load disposition options:", err);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setName(itemData?.name || "");
      setSearchTerm("");
      fetchDispositions();
    }
  }, [itemData, isOpen]);

  // Set / Map selectedValues when editing or when options finish loading
  useEffect(() => {
    if (isOpen && itemData) {
      const sourceList =
        (Array.isArray(itemData.disposition_ids) && itemData.disposition_ids.length > 0)
          ? itemData.disposition_ids
          : Array.isArray(itemData.disposition_list)
          ? itemData.disposition_list
          : [];

      const mapped = sourceList.map((item) => {
        // If it matches an option's value directly
        const byValue = availableOptions.find((opt) => opt.value === String(item));
        if (byValue) return byValue.value;

        // If returned as label from backend, convert to value
        const byLabel = availableOptions.find(
          (opt) => opt.label.toLowerCase() === String(item).toLowerCase()
        );
        if (byLabel) return byLabel.value;

        return String(item);
      });
      setSelectedValues(mapped);
    } else if (isOpen && !itemData) {
      setSelectedValues([]);
    }
  }, [availableOptions, isOpen, itemData]);

  const handleToggle = (value) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter((v) => v !== value));
    } else {
      setSelectedValues([...selectedValues, value]);
    }
  };

  const handleRemoveChip = (value) => {
    setSelectedValues(selectedValues.filter((v) => v !== value));
  };

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return availableOptions;
    const term = searchTerm.toLowerCase();
    return availableOptions.filter((opt) =>
      opt.label.toLowerCase().includes(term)
    );
  }, [availableOptions, searchTerm]);

  const handleSelectAllFiltered = () => {
    const newValues = filteredOptions.map((opt) => opt.value);
    const combined = Array.from(new Set([...selectedValues, ...newValues]));
    setSelectedValues(combined);
  };

  const handleDeselectAllFiltered = () => {
    const toRemove = new Set(filteredOptions.map((opt) => opt.value));
    setSelectedValues(selectedValues.filter((v) => !toRemove.has(v)));
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!name.trim()) {
      toast.error("Group name is required");
      return;
    }
    if (selectedValues.length === 0) {
      toast.error("Please select at least one disposition");
      return;
    }

    // Submit disposition_list using VALUE (not label)
    onSave({
      name: name.trim(),
      disposition_list: selectedValues,
    });
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={itemData ? "Edit Disposition Group" : "Add Disposition Group"}
      subtitle="Group dispositions together for campaign routing"
      icon={<Layers size={20} color="#2563eb" />}
      maxWidth="600px"
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {itemData ? "Update Group" : "Save Group"}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <div>
          <label style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px", display: "block", color: "#334155" }}>
            Group Name <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Sales Dispositions, Support Group"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>

        {/* Selected Dispositions Chips */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <label style={{ fontWeight: 600, fontSize: "13px", color: "#334155" }}>
              Selected Dispositions ({selectedValues.length})
            </label>
            {selectedValues.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedValues([])}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ef4444",
                  fontSize: "12px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Clear All
              </button>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              minHeight: "44px",
              maxHeight: "110px",
              overflowY: "auto",
              padding: "8px 10px",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
            }}
          >
            {selectedValues.length === 0 ? (
              <span style={{ fontSize: "12px", color: "#94a3b8", alignSelf: "center" }}>
                No dispositions selected yet. Select from the list below.
              </span>
            ) : (
              selectedValues.map((val) => (
                <span
                  key={val}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    backgroundColor: "#eff6ff",
                    color: "#1d4ed8",
                    border: "1px solid #bfdbfe",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "3px 10px",
                    borderRadius: "16px",
                  }}
                >
                  {optionsMap[val] || val}
                  <X
                    size={13}
                    style={{ cursor: "pointer", strokeWidth: 2.5 }}
                    onClick={() => handleRemoveChip(val)}
                  />
                </span>
              ))
            )}
          </div>
        </div>

        {/* Only Search Input */}
        <div>
          <label style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px", display: "block", color: "#334155" }}>
            Search Dispositions
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="text"
              className="form-control"
              placeholder="Type to filter dispositions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "36px" }}
            />
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>

        {/* Available Dispositions List with checkboxes */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: 600 }}>
              Available Dispositions ({filteredOptions.length})
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={handleSelectAllFiltered}
                style={{
                  background: "none",
                  border: "none",
                  color: "#2563eb",
                  fontSize: "11.5px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleDeselectAllFiltered}
                style={{
                  background: "none",
                  border: "none",
                  color: "#64748b",
                  fontSize: "11.5px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Deselect All
              </button>
            </div>
          </div>

          <div
            style={{
              maxHeight: "170px",
              overflowY: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              padding: "6px 8px",
            }}
          >
            {loadingOptions ? (
              <div style={{ textAlign: "center", padding: "16px", fontSize: "12px", color: "#64748b" }}>
                Loading dispositions...
              </div>
            ) : filteredOptions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "16px", fontSize: "12px", color: "#94a3b8" }}>
                No matching dispositions found.
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isChecked = selectedValues.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "6px 10px",
                      fontSize: "12.5px",
                      cursor: "pointer",
                      borderRadius: "6px",
                      backgroundColor: isChecked ? "#f1f5f9" : "transparent",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isChecked) e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!isChecked) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggle(opt.value)}
                      style={{ cursor: "pointer", width: "15px", height: "15px", accentColor: "#2563eb" }}
                    />
                    <span style={{ fontWeight: isChecked ? 600 : 400, color: isChecked ? "#1e40af" : "#1e293b" }}>
                      {opt.label}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddDispositionGroupModel;
